import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const isVideoUrl = (url) => {
  if (!url) return false;
  return url.match(/\.(mp4|webm|mov)$/i) || url.includes('/video/upload/');
};

const SalesGallery = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(['Semua']);
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [isLoading, setIsLoading] = useState(true);

  // Overlay state
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);
  const [direction, setDirection] = useState(0);
  
  // Filtered items
  const filteredItems = activeCategory === 'Semua' 
    ? items.slice(0, 9)
    : items.filter(item => item.category === activeCategory);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API_URL}/content/sales_gallery`);
        const galleryItems = response.data
          .filter(item => item.section.startsWith('item_'))
          .map(item => ({
            id: item.section,
            ...item.content
          }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));

        setItems(galleryItems);

        // Extract category order config if exists
        const orderConfig = response.data.find(item => item.section === 'category_order');
        const savedOrder = orderConfig?.content?.order || [];

        // Extract unique categories
        const rawCats = Array.from(new Set(galleryItems.map(item => item.category).filter(Boolean)));
        
        // Sort categories: first by savedOrder, then any remaining
        const orderedCats = [
          ...savedOrder.filter(c => rawCats.includes(c)),
          ...rawCats.filter(c => !savedOrder.includes(c))
        ];

        setCategories(['Semua', ...orderedCats]);
      } catch (error) {
        console.error('Error fetching sales gallery:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchContent();
  }, []);

  // Keyboard navigation for slider
  useEffect(() => {
    if (selectedItemIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') setSelectedItemIndex(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedItemIndex, filteredItems.length]);

  const handlePrev = useCallback(() => {
    setDirection(-1);
    setSelectedItemIndex(prev => (prev === 0 ? filteredItems.length - 1 : prev - 1));
  }, [filteredItems.length]);

  const handleNext = useCallback(() => {
    setDirection(1);
    setSelectedItemIndex(prev => (prev === filteredItems.length - 1 ? 0 : prev + 1));
  }, [filteredItems.length]);

  // Swipe for slider
  const isMultiTouchRef = useRef(false);

  useEffect(() => {
    if (selectedItemIndex === null) return;
    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length > 1) {
        isMultiTouchRef.current = true;
      }
    };
    const handleTouchEnd = (e) => {
      if (!e.touches || e.touches.length === 0) {
        setTimeout(() => {
          isMultiTouchRef.current = false;
        }, 100);
      }
    };
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [selectedItemIndex]);

  const handleDragEnd = useCallback((_event, info) => {
    if (isMultiTouchRef.current) return;
    if (Math.abs(info.offset.x) > 50) {
      if (info.offset.x < 0) handleNext();
      else handlePrev();
    }
  }, [handleNext, handlePrev]);

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="px-4 py-8 sm:py-12 max-w-7xl mx-auto">
      <div className="text-center mb-10">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-emerald-950 mb-4">Gallery Event</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">Jelajahi momen-momen berharga dan inspirasi event yang pernah diselenggarakan di Spencer Green Hotel.</p>
      </div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => {
                setActiveCategory(cat);
                setSelectedItemIndex(null);
              }}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat
                  ? 'bg-emerald-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-emerald-50 border border-gray-200'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Masonry / Grid */}
      {filteredItems.length === 0 ? (
        <div className="text-center text-gray-500 py-12 bg-white rounded-2xl">
          Belum ada foto di kategori ini.
        </div>
      ) : (
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {filteredItems.map((item, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                key={item.id}
                onClick={() => setSelectedItemIndex(index)}
                className="bg-white rounded-xl overflow-hidden shadow-soft cursor-pointer group"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  {isVideoUrl(item.image) ? (
                    <>
                      <video
                        src={`${item.image}#t=0.1`}
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-colors">
                        <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                          <div className="w-0 h-0 border-t-8 border-t-transparent border-l-[12px] border-l-white border-b-8 border-b-transparent ml-1" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      src={item.image || 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800'}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </div>
                <div className="p-5">
                  <p className="text-emerald-600 text-xs font-semibold uppercase tracking-wider mb-1">{item.category}</p>
                  <h3 className="font-display text-lg font-medium text-gray-900 mb-2">{item.title || 'Untitled Event'}</h3>
                  <p className="text-gray-500 text-sm line-clamp-2">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      )}

      {/* Fullscreen Slider Overlay */}
      <AnimatePresence>
        {selectedItemIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 relative z-10">
              <div className="text-white">
                <span className="text-gray-400 text-sm">
                  {selectedItemIndex + 1} / {filteredItems.length}
                </span>
              </div>
              <button
                onClick={() => setSelectedItemIndex(null)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content Slider */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden w-full">
              <AnimatePresence initial={false} custom={direction}>
                {selectedItemIndex !== null && filteredItems[selectedItemIndex] && (
                  <motion.div
                    key={selectedItemIndex}
                    custom={direction}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      x: { type: "spring", stiffness: 300, damping: 30 },
                      opacity: { duration: 0.2 }
                    }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={1}
                    onDragEnd={handleDragEnd}
                    className="absolute inset-0 flex flex-col items-center justify-center px-4 sm:px-12 pb-12 pt-4"
                  >
                    <div className="max-w-5xl w-full flex-1 flex items-center justify-center overflow-hidden">
                      {isVideoUrl(filteredItems[selectedItemIndex].image) ? (
                        <video
                          src={filteredItems[selectedItemIndex].image}
                          controls
                          autoPlay
                          className="w-full h-[80vh] sm:h-[85vh] object-contain rounded-lg shadow-2xl"
                          onPointerDown={(e) => e.stopPropagation()} // Prevent drag when interacting with controls
                        />
                      ) : (
                        <div onPointerDown={(e) => e.stopPropagation()} className="cursor-grab active:cursor-grabbing flex items-center justify-center">
                          <TransformWrapper
                            initialScale={1}
                            minScale={0.5}
                            maxScale={4}
                            centerOnInit
                            wheel={{ wheelDisabled: false }}
                          >
                            <TransformComponent wrapperStyle={{ width: "100%", height: "100%" }}>
                              <img
                                src={filteredItems[selectedItemIndex].image}
                                alt={filteredItems[selectedItemIndex].title}
                                className="max-w-full max-h-[80vh] sm:max-h-[85vh] object-contain rounded-lg shadow-2xl pointer-events-auto"
                              />
                            </TransformComponent>
                          </TransformWrapper>
                        </div>
                      )}
                    </div>
                    <div className="max-w-3xl w-full mt-6 text-center text-white bg-black/40 p-4 rounded-xl backdrop-blur-sm pointer-events-none">
                      <p className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-2">{filteredItems[selectedItemIndex].category}</p>
                      <h2 className="font-display text-2xl sm:text-3xl font-medium mb-2">{filteredItems[selectedItemIndex].title}</h2>
                      <p className="text-gray-300 text-sm sm:text-base line-clamp-3">{filteredItems[selectedItemIndex].description}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Nav Arrows */}
              {filteredItems.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute z-50 left-2 sm:left-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute z-50 right-2 sm:right-6 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/50 hover:bg-black/80 flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalesGallery;
